"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { dmsApi } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Upload, RefreshCw, FileText, AlertTriangle, Plus, Trash2, Folder, FolderPlus, Bell } from "lucide-react";
import { cn } from "@/lib/utils";

type MetadataField = { key: string; value: string; source?: string };

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  expiring_soon: "Expiring Soon",
  expired: "Expired",
  renewed: "Renewed",
  replaced: "Replaced",
  archived: "Archived",
  requires_review: "Requires Review",
};

function licenseDisplayName(doc: { originalFileName?: string | null; title?: string }) {
  return doc.originalFileName || doc.title || "Untitled";
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "expired") return "destructive";
  if (status === "expiring_soon") return "secondary";
  if (status === "requires_review") return "outline";
  return "default";
}

function formatLicenseNotification(item: any) {
  const payload = item?.payload || {};
  const dept = payload.departmentName ? `[${payload.departmentName}] ` : "";
  const title = payload.documentTitle || "License document";
  const days = payload.daysUntilExpiration;
  let timing = "expiration reminder";
  if (typeof days === "number") {
    if (days < 0) timing = `expired ${Math.abs(days)} day(s) ago`;
    else if (days === 0) timing = "expires today";
    else timing = `expires in ${days} day(s)`;
  }
  return `${dept}${title} — ${timing}`;
}

export function LicensesPage({
  departmentId,
  departmentName,
}: {
  departmentId?: string;
  departmentName?: string;
}) {
  const { hasPermission, isSuperAdmin, user } = useAuth();
  const canManage = isSuperAdmin || hasPermission("manage_license");
  const canDelete = isSuperAdmin || hasPermission("delete_license");
  const canConfigure = isSuperAdmin || hasPermission("configure_license_notifications");
  const canReceiveNotifications = useMemo(() => {
    if (!user) return false;
    const perms = user.permissions ?? user.role?.permissions;
    return Array.isArray(perms) && perms.includes("receive_license_notifications");
  }, [user]);

  const userDepartmentId = useMemo(() => {
    const raw = (user as { departmentId?: string | { _id?: string } } | null)?.departmentId;
    if (typeof raw === 'object' && raw?._id) return String(raw._id);
    if (typeof raw === 'string') return raw;
    return '';
  }, [user]);

  const deptParams = useMemo(() => {
    const id = isSuperAdmin ? departmentId : userDepartmentId;
    return id ? { departmentId: id } : undefined;
  }, [isSuperAdmin, departmentId, userDepartmentId]);

  const uploadDepartmentId = deptParams?.departmentId;

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [metadata, setMetadata] = useState<MetadataField[]>([]);
  const [expirationDate, setExpirationDate] = useState("");
  const [title, setTitle] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [organization, setOrganization] = useState("");
  const [issuingAuthority, setIssuingAuthority] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [responsiblePerson, setResponsiblePerson] = useState("");
  const [responsibleDepartment, setResponsibleDepartment] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [licenseFolders, setLicenseFolders] = useState<{ root: any | null; folders: any[] }>({
    root: null,
    folders: [],
  });
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [renewalOfId, setRenewalOfId] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const folderFilterParams = useMemo(() => {
    if (!selectedFolderId) return {};
    return { folderId: selectedFolderId };
  }, [selectedFolderId]);

  const uploadFolderId = useMemo(() => {
    if (selectedFolderId) return selectedFolderId;
    return licenseFolders.root?._id ? String(licenseFolders.root._id) : undefined;
  }, [selectedFolderId, licenseFolders.root]);

  const selectedFolderLabel = useMemo(() => {
    if (!selectedFolderId) return "All documents";
    const match = licenseFolders.folders.find((f) => String(f._id) === selectedFolderId);
    return match?.name || "Selected folder";
  }, [selectedFolderId, licenseFolders.folders]);

  const loadNotifications = useCallback(async () => {
    if (!canReceiveNotifications) {
      setNotifications([]);
      return;
    }
    setNotificationsLoading(true);
    try {
      const data = await dmsApi.licenses.notificationInbox({ limit: "50" });
      setNotifications(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [canReceiveNotifications]);

  const load = useCallback(async (): Promise<any[]> => {
    if (!deptParams?.departmentId) {
      setDashboard(null);
      setDocuments([]);
      setLicenseFolders({ root: null, folders: [] });
      setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      const listParams = {
        ...(deptParams || {}),
        ...folderFilterParams,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(search ? { search } : {}),
      };
      const [folderData, dash, list] = await Promise.all([
        dmsApi.licenses.folders(deptParams),
        dmsApi.licenses.dashboard({ ...(deptParams || {}), ...folderFilterParams }),
        dmsApi.licenses.list(listParams),
      ]);
      setLicenseFolders({
        root: folderData?.root || null,
        folders: Array.isArray(folderData?.folders) ? folderData.folders : [],
      });
      setDashboard(dash);
      const items = list.items || [];
      setDocuments(items);
      return items;
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Failed to load documents",
        description: e?.error || e?.message || "Unknown error",
      });
      setDashboard(null);
      setDocuments([]);
      setLicenseFolders({ root: null, folders: [] });
      return [];
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, deptParams, folderFilterParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    setReviewOpen(false);
    setSelected(null);
    setRenewalOfId(null);
    setSelectedFolderId("");
  }, [departmentId, userDepartmentId]);

  const populateReviewFromDoc = (full: any) => {
    setSelected(full);
    setTitle(full.title || "");
    setDocumentType(full.documentType || "PDF");
    setOrganization(full.organization || "");
    setIssuingAuthority(full.issuingAuthority || "");
    setLicenseNumber(full.licenseNumber || "");
    setReferenceNumber(full.referenceNumber || "");
    setResponsiblePerson(full.responsiblePerson || "");
    setResponsibleDepartment(full.responsibleDepartment || "");
    setMetadata(Array.isArray(full.metadata) ? [...full.metadata] : []);
    setExpirationDate(full.expirationDate ? String(full.expirationDate).slice(0, 10) : "");
    setReviewOpen(true);
  };

  const openReview = async (doc: any, options?: { preferLocal?: boolean }) => {
    try {
      let full = doc;
      if (doc?._id && !options?.preferLocal) {
        try {
          full = await dmsApi.licenses.get(doc._id, deptParams);
        } catch (fetchErr) {
          if (!doc?.scanStatus && !doc?.status) throw fetchErr;
          full = doc;
        }
      }
      populateReviewFromDoc(full);
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Could not open document",
        description: e?.error || e?.message,
      });
    }
  };

  useEffect(() => {
    if (!reviewOpen || !selected?._id) return;
    if (selected.scanStatus !== "pending" && selected.scanStatus !== "processing") return;

    const poll = async () => {
      try {
        const full = await dmsApi.licenses.get(selected._id, deptParams);
        const stillScanning =
          selected.scanStatus === "pending" || selected.scanStatus === "processing";
        if (
          full.scanStatus !== selected.scanStatus ||
          full.scanStage !== selected.scanStage ||
          full.scanStageMessage !== selected.scanStageMessage ||
          (stillScanning &&
            (full.scanStatus === "completed" ||
              full.scanStatus === "failed" ||
              (full.metadata?.length || 0) > (selected.metadata?.length || 0)))
        ) {
          populateReviewFromDoc(full);
        }
      } catch {
        // Keep dialog open while scan finishes in background.
      }
    };

    const id = window.setInterval(() => void poll(), 2000);
    return () => window.clearInterval(id);
  }, [
    reviewOpen,
    selected?._id,
    selected?.scanStatus,
    selected?.scanStage,
    selected?.scanStageMessage,
    selected?.metadata?.length,
    deptParams,
  ]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      toast({ variant: "destructive", title: "Only PDF files are supported" });
      return;
    }
    if (!uploadDepartmentId) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: isSuperAdmin
          ? "Select a department in the sidebar before uploading."
          : "Your user account has no department assigned.",
      });
      return;
    }
    setUploading(true);
    try {
      const doc = await dmsApi.licenses.upload(file, {
        renewalOfId: renewalOfId || undefined,
        departmentId: uploadDepartmentId,
        folderId: uploadFolderId,
      });
      setRenewalOfId(null);
      populateReviewFromDoc(doc);
      toast({ title: "License uploaded", description: "Review extracted metadata before final save." });
      void load();
    } catch (err: any) {
      let items: any[] = [];
      try {
        items = await load();
      } catch {
        items = [];
      }
      const recent = items.find(
        (d) =>
          d.status === "requires_review" &&
          (d.originalFileName === file.name || d.title === file.name.replace(/\.pdf$/i, ""))
      );
      if (recent) {
        populateReviewFromDoc(recent);
        toast({
          title: "License uploaded",
          description: "The file was saved. Review extracted metadata before final save.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: err?.error || err?.message,
        });
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const updateMetadataField = (index: number, patch: Partial<MetadataField>) => {
    setMetadata((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const addMetadataField = () => {
    setMetadata((prev) => [...prev, { key: "", value: "", source: "manual" }]);
  };

  const removeMetadataField = (index: number) => {
    setMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const applyCandidateDate = (isoDate: string) => {
    setExpirationDate(isoDate.slice(0, 10));
  };

  const handleConfirm = async () => {
    if (!selected) return;
    if (!expirationDate) {
      toast({ variant: "destructive", title: "Expiration date is required" });
      return;
    }
    setSaving(true);
    try {
      await dmsApi.licenses.confirmMetadata(
        selected._id,
        {
          title,
          documentType,
          organization,
          issuingAuthority,
          licenseNumber,
          referenceNumber,
          responsiblePerson,
          responsibleDepartment,
          expirationDate,
          expirationDateSource: selected.expirationDateSource || "manual",
          metadata: metadata.filter((m) => m.key.trim()),
        },
        deptParams
      );
      toast({ title: "License metadata saved" });
      setReviewOpen(false);
      setSelected(null);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: err?.error || err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dmsApi.licenses.delete(deleteTarget._id, deptParams);
      toast({ title: "Document deleted", description: "The license and PDF file were removed." });
      if (selected?._id === deleteTarget._id) {
        setReviewOpen(false);
        setSelected(null);
      }
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: err?.error || err?.message,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast({ variant: "destructive", title: "Folder name is required" });
      return;
    }
    if (!licenseFolders.root?._id) {
      toast({ variant: "destructive", title: "Documents folder is not ready yet" });
      return;
    }
    const parentId = selectedFolderId || String(licenseFolders.root._id);
    setCreatingFolder(true);
    try {
      const folder = await dmsApi.licenses.createFolder(
        { name: newFolderName.trim(), parentId },
        deptParams
      );
      toast({ title: "Folder created", description: folder.name });
      setNewFolderOpen(false);
      setNewFolderName("");
      setSelectedFolderId(String(folder._id));
      await load();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Could not create folder",
        description: err?.error || err?.message,
      });
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRetryScan = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const doc = await dmsApi.licenses.retryScan(selected._id, deptParams);
      await openReview(doc);
      toast({ title: "Scan completed", description: "Review extracted fields." });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Scan failed",
        description: err?.error || err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const counts = dashboard?.counts || {};
  const upcoming = dashboard?.upcomingExpirations || [];

  const candidates = useMemo(() => {
    if (!selected?.expirationCandidates?.length) return [];
    return selected.expirationCandidates.map((c: any) => ({
      label: c.label,
      date: String(c.date).slice(0, 10),
      confidence: c.confidence,
    }));
  }, [selected]);

  if (isSuperAdmin && !departmentId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50" />
        <h2 className="text-lg font-semibold">Select a department</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Choose a department from the sidebar, then open Documents to view that department&apos;s documents only.
        </p>
      </div>
    );
  }

  if (!isSuperAdmin && !userDepartmentId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50" />
        <h2 className="text-lg font-semibold">No department assigned</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Your user account must be assigned to a department before you can manage documents.
        </p>
      </div>
    );
  }

  if (loading && !dashboard) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b bg-white/60 p-4 backdrop-blur-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Document Management</h1>
            <p className="text-sm text-muted-foreground">
              {isSuperAdmin && departmentName
                ? `Showing documents for ${departmentName} only. Change department in the sidebar to view another.`
                : "Track documents, permits, certificates, and expiration dates."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                load();
                loadNotifications();
              }}
              disabled={loading}
            >
              <RefreshCw size={14} className="mr-1" />
              Refresh
            </Button>
            {canManage && (
              <label className="inline-flex cursor-pointer">
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleUpload} />
                <Button size="sm" disabled={uploading} asChild>
                  <span>
                    {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
                    Upload PDF
                  </span>
                </Button>
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          {[
            ["Total", counts.total],
            ["Active", counts.active],
            ["Expiring Soon", counts.expiringSoon],
            ["Expired", counts.expired],
            ["Needs Review", counts.requiresReview],
            ["Scan Errors", counts.scanFailed],
            ["Missing Expiry", counts.missingExpiration],
          ].map(([label, value]) => (
            <Card key={String(label)}>
              <CardHeader className="p-3 pb-1">
                <CardDescription className="text-xs">{label}</CardDescription>
                <CardTitle className="text-2xl">{value ?? 0}</CardTitle>
              </CardHeader>
            </Card>
          ))}
        </div>

        {canReceiveNotifications && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell size={16} />
                  Expiration Notifications
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={loadNotifications} disabled={notificationsLoading}>
                  <RefreshCw size={14} className={notificationsLoading ? "animate-spin" : ""} />
                </Button>
              </div>
              <CardDescription className="text-xs">
                {isSuperAdmin
                  ? "All departments — each alert includes the department name."
                  : "Alerts for licenses in your department only."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-48 overflow-y-auto">
              {notificationsLoading && notifications.length === 0 ? (
                <p className="text-sm text-muted-foreground">Loading notifications...</p>
              ) : notifications.length === 0 ? (
                <p className="text-sm text-muted-foreground">No expiration alerts right now.</p>
              ) : (
                notifications.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className="w-full rounded border p-2 text-left text-sm hover:bg-muted/40"
                    onClick={() => openReview({ _id: item.payload?.documentId || item.licenseDocumentId })}
                  >
                    <div className="font-medium">{formatLicenseNotification(item)}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {item.payload?.expirationDate ? `Expiration: ${item.payload.expirationDate}` : ""}
                      {item.createdAt ? ` · ${String(item.createdAt).slice(0, 10)}` : ""}
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <Card className="xl:col-span-1">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">Folders</CardTitle>
                {canManage && (
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setNewFolderOpen(true)}>
                    <FolderPlus size={14} className="mr-1" />
                    New
                  </Button>
                )}
              </div>
              <CardDescription className="text-xs">
                Organize documents into categories like Contracts, Servers, etc.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              <button
                type="button"
                onClick={() => setSelectedFolderId("")}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted/50",
                  !selectedFolderId && "bg-primary/10 text-primary font-medium"
                )}
              >
                <Folder size={14} className="shrink-0" />
                <span className="truncate">All documents</span>
              </button>
              {licenseFolders.folders
                .filter((folder) => !folder.isRoot)
                .map((folder) => (
                  <button
                    key={folder._id}
                    type="button"
                    onClick={() => setSelectedFolderId(String(folder._id))}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted/50",
                      selectedFolderId === String(folder._id) && "bg-primary/10 text-primary font-medium"
                    )}
                    style={{ paddingLeft: `${8 + Math.max(folder.depth || 1, 1) * 12}px` }}
                  >
                    <Folder size={14} className="shrink-0" />
                    <span className="truncate">{folder.name}</span>
                  </button>
                ))}
              {licenseFolders.folders.filter((folder) => !folder.isRoot).length === 0 && (
                <p className="text-xs text-muted-foreground px-2 py-2">
                  No subfolders yet. Create folders to group documents.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="xl:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Documents</CardTitle>
              <CardDescription className="text-xs">
                Showing: {selectedFolderLabel}
              </CardDescription>
              <div className="flex flex-wrap gap-2 pt-2">
                <Input
                  placeholder="Search title, license #, reference..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-xs h-9"
                />
                <select
                  className="h-9 rounded-md border px-2 text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All statuses</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
                <Button size="sm" variant="outline" onClick={load}>
                  Apply
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {documents.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No documents yet.</p>
              ) : (
                documents.map((doc) => (
                  <div
                    key={doc._id}
                    className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/30"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-primary shrink-0" />
                        <span className="font-medium truncate" title={licenseDisplayName(doc)}>
                          {licenseDisplayName(doc)}
                        </span>
                        <Badge variant={statusVariant(doc.status)}>{STATUS_LABELS[doc.status] || doc.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {doc.originalFileName && doc.title && doc.title !== doc.originalFileName.replace(/\.pdf$/i, "")
                          ? `Title: ${doc.title} · `
                          : ""}
                        {doc.documentType || "PDF"}
                        {doc.licenseNumber ? ` · ${doc.licenseNumber}` : ""}
                        {doc.expirationDate
                          ? ` · Expires ${String(doc.expirationDate).slice(0, 10)}`
                          : " · No expiration confirmed"}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {canManage && (
                        <Button size="sm" variant="outline" onClick={() => openReview(doc)}>
                          Review
                        </Button>
                      )}
                      {canManage && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setRenewalOfId(doc._id);
                            document.querySelector<HTMLInputElement>('input[type="file"][accept*="pdf"]')?.click();
                          }}
                        >
                          Renew
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(doc)}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Upcoming Expirations</CardTitle>
              <CardDescription>Within {dashboard?.expiringSoonThresholdDays ?? 30} days</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming expirations.</p>
              ) : (
                upcoming.map((doc: any) => (
                  <div key={doc._id} className="rounded border p-2 text-sm">
                    <div className="font-medium truncate" title={licenseDisplayName(doc)}>
                      {licenseDisplayName(doc)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {String(doc.expirationDate).slice(0, 10)} · {doc.daysUntilExpiration} days left
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review License Metadata</DialogTitle>
            <DialogDescription>
              {selected?.originalFileName
                ? `File: ${selected.originalFileName}`
                : "Confirm extracted fields and expiration date before final save."}
            </DialogDescription>
          </DialogHeader>

          {(selected?.scanStatus === "pending" || selected?.scanStatus === "processing") && (
            <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mt-0.5 shrink-0" />
              <p className="font-medium text-foreground">
                {selected.scanStageMessage ||
                  (selected.scanStage === "ocr"
                    ? "Reading document…"
                    : selected.scanStage === "ai"
                      ? "Extracting document details…"
                      : "Preparing document…")}
              </p>
            </div>
          )}

          {selected?.scanStatus === "completed" && selected?.expirationDate && !selected?.expirationDateConfirmed && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-muted-foreground">
              Fields were pre-filled from the document. Confirm the expiration date before saving.
            </div>
          )}

          {selected?.scanStatus === "failed" && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
              <div>
                <p className="font-medium">Could not read document automatically</p>
                <p className="text-muted-foreground">
                  Please fill in the fields manually, or try again.
                </p>
                {canManage && (
                  <Button size="sm" variant="outline" className="mt-2" onClick={handleRetryScan} disabled={saving}>
                    Try again
                  </Button>
                )}
              </div>
            </div>
          )}

          {selected?.dmsFileId && (
            <div className="rounded border p-2">
              <iframe
                title="License PDF preview"
                src={dmsApi.licenses.getFileViewUrl(selected.dmsFileId)}
                className="w-full h-48 rounded"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>Document Type</Label>
              <Input value={documentType} onChange={(e) => setDocumentType(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>License Number</Label>
              <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>Organization</Label>
              <Input value={organization} onChange={(e) => setOrganization(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>Issuing Authority</Label>
              <Input value={issuingAuthority} onChange={(e) => setIssuingAuthority(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>Responsible Person</Label>
              <Input value={responsiblePerson} onChange={(e) => setResponsiblePerson(e.target.value)} disabled={!canManage} />
            </div>
            <div>
              <Label>Responsible Department</Label>
              <Input
                value={responsibleDepartment}
                onChange={(e) => setResponsibleDepartment(e.target.value)}
                disabled={!canManage}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Expiration Date <span className="text-destructive">*</span>
            </Label>
            <Input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              disabled={!canManage}
            />
            {candidates.length > 0 && (
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Detected date candidates:</p>
                {candidates.map((c, i) => (
                  <button
                    key={`${c.date}-${i}`}
                    type="button"
                    className="block text-left text-primary hover:underline"
                    onClick={() => applyCandidateDate(c.date)}
                  >
                    {c.label}: {c.date} (confidence {Math.round((c.confidence || 0) * 100)}%)
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Dynamic Metadata Fields</Label>
              {canManage && (
                <Button size="sm" variant="outline" onClick={addMetadataField}>
                  <Plus size={14} className="mr-1" />
                  Add field
                </Button>
              )}
            </div>
            {metadata.map((field, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                <Input
                  className="col-span-4"
                  placeholder="Field name"
                  value={field.key}
                  onChange={(e) => updateMetadataField(index, { key: e.target.value, source: "manual" })}
                  disabled={!canManage}
                />
                <Input
                  className="col-span-7"
                  placeholder="Value"
                  value={field.value}
                  onChange={(e) => updateMetadataField(index, { value: e.target.value, source: "manual" })}
                  disabled={!canManage}
                />
                {canManage && (
                  <Button variant="ghost" size="icon" onClick={() => removeMetadataField(index)}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {canDelete && selected && (
                <Button
                  variant="destructive"
                  onClick={() => setDeleteTarget(selected)}
                  disabled={saving || deleting}
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReviewOpen(false)}>
                Close
              </Button>
              {canManage && (
                <Button onClick={handleConfirm} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & Save"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete license document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <span className="font-medium">{deleteTarget ? licenseDisplayName(deleteTarget) : "this document"}</span>{" "}
              and move its PDF file to trash. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create document subfolder</DialogTitle>
            <DialogDescription>
              {selectedFolderId
                ? `New folder will be created inside "${selectedFolderLabel}".`
                : `New folder will be created inside the Documents folder.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="license-folder-name">Folder name</Label>
            <Input
              id="license-folder-name"
              placeholder="e.g. Contracts, Servers, Certificates"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={creatingFolder}>
              {creatingFolder ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create folder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
